/* eslint-disable @typescript-eslint/no-explicit-any */
import { CustomerTypeEnum } from './common.types';
import { SalesResponsible } from './user.types';

export { CustomerTypeEnum } from './common.types';

// Main customer interface
export interface Customer {
  Name: string;
  GivenName: string;
  CustomerId: string;
  CustomerNo: number;
  CustomerAddress: string;
  SalesResponsible: SalesResponsible | null;
  ServiceAgent: any | null;
  CustomerPaymentTerms: PaymentTerm[];
  CustomerInvoicingDelivery: InvoicingDelivery[];
  CustomerPrimaryCommunication: PrimaryCommunication[];
  DirectDebit: boolean;
  Guardian: any | null;
  Estate: boolean;
  CustomerCommunication: any[];
  CustomerMarking: any | null;
  LegalEntityType: LegalEntityType[];
  CustomerRelationType: RelationType[];
  CustomerJourneyType: JourneyType[];
  LegalEntityValue: string;
  AlternateOrgId: string | null;
  BankOrgId: string | null;
  ZynergyCustomerOverViewUrl: string;
  OldCustomerId: string | null;
  CustomerStoppedForInvoice: boolean;
  CustomerStoppedForInvoiceReason: string | null;
  Attributes: CustomerAttributes;
  CustomerGroups: CustomerGroup[];
  HasExternalUser: boolean;
  NumberOfClaimLocks: number;
  CustomerType: CustomerTypeEnum;
  SendNoConfirmation: boolean;
  AutomaticAnvisning: boolean;
  ZynergyCustomerTeam: ZynergyTeam[];
  ZynergyInvoiceGroup: ZynergyInvoiceGroup[];
  CustomerSegments: CustomerSegment[];
  ContactPersons: any | null;
  Age: number;
  CustomerMissingInZynergy: boolean;
  EInvoicePayByOther: boolean;
  Companies: any[];
  DisregardAutomaticWork: boolean;
  HasApsisEvents: boolean;
  BrazeCustomer: boolean;
  SalesChannelName: string;
  ExternalPartnerName: string | null;
  Created: string;
  EnergyCompanies: any | null;
  LocalDb: number;
  Zynergy: number;
  Mappings: number;
}

// Customer model for search results
export interface CustomerModel {
  EntityNo: string;
  CompanyId: number;
  CompanyName: string;
  CustomerNo: number;
  CustomerId: string;
  CustomerName: string;
  CrmGuid: string;
  LegalPersonNumber: string;
  EMail: string;
  Address: string;
  CustomerType: CustomerTypeEnum;
}

// Customer note
export interface CustomerNote {
  Id: number;
  Type: number;
  Name: string | null;
  Time: string;
  Modified: string;
  Created: string;
  Data: string;
  ChangedBy: {
    Id: number;
    Name: string;
  };
  CreatedBy: {
    Id: number;
    Name: string;
  };
  Status: boolean;
  ParentEventDataId: number | null;
}

// Customer attributes
export interface CustomerAttributes {
  AnvisningAutomaticHandling: boolean;
  ConfirmationDelivery: number;
  CustomerCollectConfirmation: boolean;
  CustomerConsentFunctional: boolean | null;
  CustomerConsentMarketing: boolean | null;
  CustomerConsentTracking: boolean | null;
  CustomerEInvoiceBankId: string | null;
  CustomerEInvoiceSSN: string | null;
  EnableAdvancedStatistics: boolean | null;
  LatestViewedInvoiceNo: string | null;
}

// Customer segments
export interface CustomerSegment {
  Id: number | null;
  Name: string;
  Description: string;
  Selected: boolean;
}

// Customer groups
export interface CustomerGroup {
  Id: number;
  Name: string;
  SelectedValue: string | null;
  PropertyName: string | null;
  Alternatives: CustomerGroupAlternative[];
}

export interface CustomerGroupAlternative {
  Id: number;
  Name: string;
}

// Payment and invoicing
export interface PaymentTerm {
  PaymentTermNo: number;
  Id: number;
  Name: string;
  Selected: boolean;
  ShowInCrm: boolean;
}

export interface InvoicingDelivery {
  InvoicingDeliveryNo: number;
  Id: number;
  Name: string;
  Selected: boolean;
  ShowInCrm: boolean;
}

export enum CustomerInvoicingDeliveryType {
  Letter = 1,
  EInvoice = 2,
  SVEInvoice = 3,
  EmailInvoice = 4,
  OnlyPDF = 5,
  LocalInvoice = 6,
  InternallyCredited = 7,
}

// Communication
export interface PrimaryCommunication {
  CommunicationName: string;
  CommunicationId: number;
  Selected: boolean;
}

// Legal entity
export interface LegalEntityType {
  LegalEntityTypeName: string;
  LegalEntityType: number;
  LegalEntityTypeId: string;
  LegalEntityTypeCustomerGroup: string;
  ChangeableTo: number[];
  Selected: boolean;
}

// Relations
export interface RelationType {
  RelationTypeName: string;
  RelationType: number;
  Selected: boolean;
}

// Journey
export interface JourneyType {
  JourneyTypeId: number;
  Name: string;
  Selected: boolean;
}

// Zynergy
export interface ZynergyTeam {
  Value: string;
  Description: string;
  Selected: boolean;
}

export interface ZynergyInvoiceGroup {
  Value: string;
  Description: string;
  Selected: boolean;
  Selectable: boolean;
}

// Search and create requests/responses
export interface SearchCustomer {
  CustomerName?: string;
  CustomerEntityOrgId?: string;
  NewCustomerId?: string;
  CustomerEMaill?: string;
  PhoneNumber?: string;
}

export interface CreditsafeAddress {
  Street: string;
  ZipCode: string;
  Town: string;
  AddressType: string;
}

export interface CreditsafeReturnModel {
  OrgNr: string;
  Name: string;
  OldName: string | null;
  Address: CreditsafeAddress | null;
  OldAddress: CreditsafeAddress | null;
  StatusType: string;
  StatusText: string;
  IsPartOfGroup: boolean;
  CompanyType: string | null;
  CompanyTypeText: string | null;
  CompanyLegalGroup: string | null;
  EntityNo: number;
}

export interface CustomerSearchResponse {
  CustomerReturnModel: CustomerModel[];
  HasMore: boolean;
  CustomerInvoices: unknown | null;
  CreditsafeReturnModel: CreditsafeReturnModel[];
  SynaError: string | null;
  MaybeExistingCustomers: CustomerModel[];
}

export interface CreateSuspectRequest {
  orgId: string;
}

export interface CreateSuspectResponse {
  CustomerId: number;
  success: boolean;
  message?: string;
}

export interface UpdateCustomerNoteRequest {
  customerId: string | number;
  data: string;
}